import { useEffect, useMemo, type ReactNode } from "react";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { parsedAny } from "@thalamos/common";
import type {
  AvailableOrganisationArray,
  ExtendedVisionUser,
  OrganisationId,
} from "@thalamos/vision-common";
import {
  AvailableOrganisationArraySchema,
  isCurrentUserSupervisor,
} from "@thalamos/vision-common";

import { isRefusal } from "../../api/axios/api.js";
import api, { apiHooks } from "../../api/index.js";
import type { UserContextType } from "../../Context/UserContext.js";
import { UserProvider, useUser } from "../../Context/UserContext.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import i18n from "../../i18n.js";
import { TrainingBanner } from "../../layout/TrainingBanner.js";
import { Error } from "../../pages/Error/Error.js";
import { createRoute } from "../../routing/createRoute.js";
import { OrganisationContext } from "../FormBuilder/OrganisationDropdown/OrganisationContext.js";
import { PlacesOfSafetyProvider } from "../FormBuilder/PlaceOfSafetyPicker/PlacesOfSafetyProvider.js";
import { MainLayout } from "../MainLayout/MainLayout.js";
import { Onboarding } from "../Onboarding/Onboarding.js";

export const RootComponent = () => {
  return (
    <UserProvider>
      <RootComponentContent />
    </UserProvider>
  );
};

const RootComponentContent = () => {
  const {
    user,
    loading: userLoading,
    error: userError,
    refetchUser,
  } = useUser();
  const navigate = useNavigate();
  const notifications = useToastNotifications();
  const { t } = useTranslation();

  useEffect(() => {
    if (user?.sessionOrganisationConfiguration?.language) {
      void i18n.changeLanguage(user.sessionOrganisationConfiguration.language);
    }
  }, [user?.sessionOrganisationConfiguration?.language]);

  useEffect(() => {
    async function checkUserRoles() {
      if (!user) {
        return;
      }
      const userHasMultipleRoles = user.memberships.length > 1;
      if (!user.sessionContext) {
        if (userHasMultipleRoles) {
          navigate(createRoute.contextSelector());
        } else {
          const selectedMembership = user.memberships[0];
          if (selectedMembership === undefined) {
            notifications.show(t("contextSelector.setContextError"), {
              severity: "error",
            });
            return;
          }
          const response = await api.user.setSessionContext(selectedMembership);
          if (response.status !== 204) {
            notifications.show(t("contextSelector.setContextError"), {
              severity: "error",
            });
          } else {
            await refetchUser();
          }
        }
      }
    }
    void checkUserRoles();
  }, [user, navigate, notifications, t]);

  if (userLoading || user === undefined) {
    return <CircularProgress />;
  }

  if (userError) {
    return <Error />;
  }

  return <RootComponentInner user={user} refetchUser={refetchUser} />;
};

export const RootComponentInner = ({
  user,
  refetchUser,
}: {
  user: ExtendedVisionUser;
  refetchUser: UserContextType["refetchUser"];
}) => {
  const organisationId = user.sessionContext?.organisationId;

  return (
    organisationId && (
      <OrganisationFetcher
        organisationId={organisationId}
        user={user}
        refetchUser={refetchUser}
      />
    )
  );
};

const OrganisationFetcher = ({
  organisationId,
  user,
  refetchUser,
}: {
  organisationId: OrganisationId;
  user: ExtendedVisionUser;
  refetchUser: UserContextType["refetchUser"];
}) => {
  const [{ data: organisationsResponse, loading, error }] =
    apiHooks.organisation.getAvailableOrganisations(organisationId);

  if (loading) {
    return <CircularProgress />;
  }

  // A refused list is treated as empty: better no organisation to pick than a
  // blocked app. Only a failure to answer stops it.
  if (error && !isRefusal(error)) {
    return <Error />;
  }

  // Parse organisations data

  const availableOrganisations = parsedAny(
    AvailableOrganisationArraySchema,
    organisationsResponse,
  ).unwrapOr([]) as AvailableOrganisationArray;

  return (
    <RootComponentInnerContent
      user={user}
      refetchUser={refetchUser}
      availableOrganisations={availableOrganisations}
    />
  );
};

export const RootComponentInnerContent = ({
  user,
  refetchUser,
  availableOrganisations,
  mainLayout,
}: {
  user: ExtendedVisionUser;
  refetchUser: UserContextType["refetchUser"];
  availableOrganisations: AvailableOrganisationArray;
  mainLayout?: ReactNode;
}) => {
  const organisations = useMemo(
    () => ({ organisations: availableOrganisations }),
    [availableOrganisations],
  );

  const showMainLayout =
    // Regular users must be onboarded.
    user.isOnboarded ||
    // Admins don't need to be onboarded.
    user.isAdminSession ||
    // Supervisors don't need to be onboarded.
    isCurrentUserSupervisor(user);

  const showTrainingBanner =
    user.sessionOrganisationConfiguration?.isTrainingOrganisation;

  return (
    <OrganisationContext.Provider value={organisations}>
      <PlacesOfSafetyProvider>
        {showMainLayout ? (
          <>
            {showTrainingBanner && <TrainingBanner />}
            {mainLayout ?? <MainLayout user={user} refetchUser={refetchUser} />}
          </>
        ) : (
          <Onboarding user={user} refetchUser={refetchUser} />
        )}
      </PlacesOfSafetyProvider>
    </OrganisationContext.Provider>
  );
};
